// @flow
import React from 'react';
import classNames from 'classnames';
import { Field } from 'formik';

import { ICONS } from 'Components/utils/icon/icons';
import { Icon } from 'Components/frameworks/icon/Icon.jsx';

type GroupedFieldProps = {
  fieldName: string,
  position: 'left' | 'right',
  width: string,
};

type GroupedSelectProps = {
  ...$Exact<GroupedFieldProps>,
  onChange?: (e: *) => void,
  options: Array<{
    value: number | string,
    name: string,
  }>,
};

type GroupedInputProps = {
  ...$Exact<GroupedFieldProps>,
  type?: string,
  required?: boolean,
  placeholder?: string,
};

export function GroupedSelect({
  fieldName,
  position,
  width,
  options,
  onChange,
}: GroupedSelectProps) {
  return (
    <Field name={fieldName}>
      {({ field, form: { errors } }) => (
        <div
          className={classNames(`relative inline-block w-${width} border-1 overflow-hidden`, {
            'border-r-0': position === 'left',
            'rounded-l-small': position === 'left',
            'rounded-r-small': position === 'right',
            'border-emerald': !errors[field.name],
            'border-red': errors[field.name],
          })}
        >
          <select
            autoComplete="off"
            className="reset-input reset-select relative text-current w-full bg-white px-3 py-2"
            {...field}
            {...(onChange ? { onChange } : null)}
          >
            {options.map(({ value, name }) => (
              <option key={name} value={value}>
                {name}
              </option>
            ))}
          </select>
          <div className="absolute flex h-full items-center top-0 rtl:right-0 pointer-events-none px-2 text-current">
            <Icon name={ICONS.SELECT} width="0.75rem" />
          </div>
        </div>
      )}
    </Field>
  );
}

export function GroupedInput({
  fieldName,
  position,
  width,
  type = 'text',
  required = false,
}: GroupedInputProps) {
  return (
    <Field name={fieldName}>
      {({ field, form: { touched, errors } }) => (
        <input
          type={type}
          required={required}
          {...field}
          className={classNames(
            `outline-none shadows-none inline-block w-${width} px-3 py-2 align-top text-current bg-white border-1`,
            {
              'border-r-0': position === 'left',
              'rounded-l-small': position === 'left',
              'rounded-r-small': position === 'right',
              'border-gray-medium': !touched[field.name],
              'border-emerald': touched[field.name] && !errors[field.name],
              'border-red': touched[field.name] && errors[field.name],
            },
          )}
        />
      )}
    </Field>
  );
}
